"use client";

import { useEffect, useState } from "react";
import { toast } from "react-toastify";

const ISSUE_TYPES = [
  { value: "شكوى", label: "الإبلاغ عن مشكلة" },
  { value: "اقتراح", label: "طلب ميزة / تحسين" },
  { value: "عام", label: "سؤال أو طلب آخر" },
];

export default function FloatingBugReport({
  showFloatingButton = true,
  showInlineTrigger = false,
  inlineTriggerClassName = "btn btn-outline-secondary btn-sm",
  inlineTriggerLabel = "تواجه مشكلة ؟ دعنا نساعدك",
}) {
  const baseURL = process.env.NEXT_PUBLIC_BASE_URL;
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentPageUrl, setCurrentPageUrl] = useState("");
  const [form, setForm] = useState({
    type: ISSUE_TYPES[0].value,
    name: "",
    email: "",
    message: "",
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    setCurrentPageUrl(window.location.href || "");

    const token = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");

    if (!token || !storedUser) return;

    try {
      const user = JSON.parse(storedUser);
      const possibleName =
        user?.name ||
        user?.display_name ||
        user?.user_display_name ||
        user?.username ||
        "";
      const possibleEmail = user?.email || user?.user_email || "";

      setIsLoggedIn(true);
      setForm((prev) => ({
        ...prev,
        name: prev.name || possibleName,
        email: prev.email || possibleEmail,
      }));
    } catch (error) {
      setIsLoggedIn(false);
    }
  }, []);

  const resetForm = () => {
    setForm({
      type: ISSUE_TYPES[0].value,
      name: "",
      email: "",
      message: "",
    });
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  const openModal = () => {
    setIsOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!baseURL) {
      toast.error("تعذر الإرسال حالياً، يرجى المحاولة لاحقاً");
      return;
    }

    setIsSubmitting(true);

    try {
      const enrichedMessage = currentPageUrl
        ? `${form.message.trim()}\n\nرابط الصفحة الحالية: ${currentPageUrl}`
        : form.message.trim();

      const response = await fetch(`${baseURL}api/ticket/v1/ticket`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          type: form.type,
          message: enrichedMessage,
          page_url: currentPageUrl,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to submit report");
      }

      toast.success("تم إرسال رسالتك بنجاح، شكراً لك");
      resetForm();
      closeModal();
    } catch (error) {
      toast.error("حدث خطأ أثناء الإرسال، حاول مرة أخرى");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {showFloatingButton && (
        <div className="floating-bug-button">
          <button
            type="button"
            className="btn btn-danger btn-lg rounded-circle shadow-lg"
            title="الإبلاغ عن مشكلة"
            aria-label="الإبلاغ عن مشكلة أو طلب"
            onClick={openModal}
          >
            <i className="bi bi-bug-fill" />
          </button>
        </div>
      )}

      {showInlineTrigger && (
        <div className="d-inline-block">
          <button
            type="button"
            className={inlineTriggerClassName}
            onClick={openModal}
          >
            <i className="bi bi-life-preserver me-2" />
            {inlineTriggerLabel}
          </button>
        </div>
      )}

      {isOpen && (
        <div
          className="bug-report-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="نموذج الإبلاغ عن مشكلة"
          onClick={closeModal}
        >
          <div className="bug-report-content bg-light" onClick={(event) => event.stopPropagation()}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0 text-dark">
                <i className="bi bi-bug me-2" />
                بلّغ عن مشكلة أو اطلب ميزة
              </h6>
              <button type="button" className="btn-close" aria-label="إغلاق" onClick={closeModal} />
            </div>

            <form className="d-grid gap-2" onSubmit={handleSubmit}>
              <label className="form-label mb-0" htmlFor="bug-type">
                نوع الرسالة
              </label>
              <select
                id="bug-type"
                className="form-select"
                value={form.type}
                onChange={(event) => setForm((prev) => ({ ...prev, type: event.target.value }))}
                required
              >
                {ISSUE_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>

              <input
                type="text"
                className="form-control"
                placeholder="الاسم"
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                autoComplete="name"
                readOnly={isLoggedIn}
                required
              />

              <input
                type="email"
                className="form-control"
                placeholder="البريد الإلكتروني"
                value={form.email}
                onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                autoComplete="email"
                readOnly={isLoggedIn}
                required
              />

              <textarea
                className="form-control"
                rows={4}
                placeholder="اكتب المشكلة أو الطلب بسرعة..."
                value={form.message}
                onChange={(event) => setForm((prev) => ({ ...prev, message: event.target.value }))}
                required
              />

              <button type="submit" className="btn btn-dark mt-1" disabled={isSubmitting}>
                {isSubmitting ? "جاري الإرسال..." : "إرسال"}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
